import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ContextCard from './ContextCard';
import { getCollabWhere, readViewState, avatarStyle } from '../utils/collab';
import { formatStamp, parseStamp } from '../utils/datetime';

/**
 * The docked conversation, bottom-right.
 *
 * Four things here are load-bearing rather than decorative:
 *
 * * **The refusal is shown, not swallowed.** When the recipient has chat turned off the
 *   server answers 403 with a sentence ("Ruth has chat turned off."), and that sentence
 *   is printed above the composer. A send that silently goes nowhere is worse than no
 *   chat at all, because the sender believes it arrived.
 * * **The retention line is not filler.** Chat is the one thing in this database Orcanos
 *   cannot rebuild (Critical Note #70), and the number of days is a promise the sweep
 *   actually keeps. It is stated where people write, not buried in a settings screen.
 * * **The scroll position is managed on purpose.** A chat that always jumps to the bottom
 *   makes "Load earlier messages" useless — the reader is thrown back to today the instant
 *   the older page arrives. Every render decides between three intents (stay at the
 *   bottom, hold the reader's place, go to a searched-for message) rather than defaulting
 *   to one; see `scrollIntentRef`.
 * * **Edit and delete are the SERVER's decision.** The buttons hide themselves past the
 *   day-long window, but that is a courtesy — the window is enforced in `/api/collab/edit`
 *   and the refusal is printed, so a clock-skewed browser gets a sentence and not a
 *   silently failed save.
 */

/** The three shapes a message can be in on screen. */
const isTombstone = (m) => !!m.deleted_at;

function ChatDock({ person, me, thread = [], meta, pendingContext, retentionDays = 90,
                    editWindowSeconds = 86400,
                    onClose, onSend, onLoadEarlier, onSearch, onJumpToMessage,
                    onJumpToLatest, onAnchorShown, onEdit, onDelete }) {
  const location = useLocation();
  const [text, setText] = useState('');
  const [attached, setAttached] = useState(pendingContext || null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Search
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);   // null = nothing asked yet
  const [searching, setSearching] = useState(false);

  // Editing one of my own messages, in place
  const [editingId, setEditingId] = useState('');
  const [editText, setEditText] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState('');
  const [loadingEarlier, setLoadingEarlier] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const bodyIdRef = useRef({});   // message id -> its DOM node, for scrolling to a hit

  const hasMoreBefore = !!meta?.hasMoreBefore;
  const hasMoreAfter = !!meta?.hasMoreAfter;
  const anchorId = meta?.anchorId || '';

  /**
   * What the NEXT layout pass should do with the scroll position.
   *
   * `{ type: 'bottom' }`  — the ordinary case: a new message, or the thread just opened.
   * `{ type: 'hold', height }` — an older page was prepended; keep the reader looking at
   *                        the same message by adding back exactly the height that grew
   *                        above them. Anything else scrolls them somewhere they did not
   *                        ask to be.
   * `{ type: 'anchor', id }` — a search hit: put that message on screen and flash it.
   */
  const scrollIntentRef = useRef({ type: 'bottom' });

  useEffect(() => { setAttached(pendingContext || null); }, [pendingContext]);

  // A different person is a different conversation: every transient thing about the old
  // one — a half-typed message, an open search, a message mid-edit — belongs to it and
  // must not follow the reader into somebody else's thread.
  useEffect(() => {
    setError('');
    setText('');
    setSearchOpen(false);
    setQuery('');
    setResults(null);
    setEditingId('');
    setPendingDeleteId('');
    scrollIntentRef.current = { type: 'bottom' };
  }, [person?.user_id]);

  useEffect(() => { inputRef.current?.focus(); }, [person?.user_id]);
  useEffect(() => { if (searchOpen) searchRef.current?.focus(); }, [searchOpen]);

  // New content arrived: whatever intent was set when it was asked for, act on it once.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const intent = scrollIntentRef.current || { type: 'bottom' };
    scrollIntentRef.current = { type: 'bottom' };

    if (intent.type === 'hold') {
      el.scrollTop = el.scrollHeight - intent.height;
      return;
    }
    if (intent.type === 'anchor') {
      const node = bodyIdRef.current[intent.id];
      if (node) {
        node.scrollIntoView({ block: 'center' });
        return;
      }
    }
    el.scrollTop = el.scrollHeight;
  }, [thread, attached]);

  // The flash on a searched-for message is shown once and then forgotten, so scrolling
  // away and back does not re-highlight a message nobody is looking for any more.
  useEffect(() => {
    if (!anchorId) return;
    const t = setTimeout(() => onAnchorShown?.(person.user_id), 2200);
    return () => clearTimeout(t);
  }, [anchorId, person?.user_id, onAnchorShown]);

  /** What "Share this view" attaches: the route and the panel this tab is on.
   *  The TITLE is deliberately absent — the reader's copy of this card gets its title
   *  resolved server-side under their own privacy scope (Critical Note #71). */
  const buildCurrentView = () => {
    const where = getCollabWhere();
    if (!where.panelId && !where.module) return null;
    return {
      kind: 'view',
      module: where.module,
      route: location.pathname,
      panel_id: where.panelId,
      state: readViewState(),   // page, expanded branches — read at click time
    };
  };
  const canShareView = useMemo(() => {
    const where = getCollabWhere();
    return !!(where.panelId || where.module);
  }, [location.pathname]);

  /** May this message still be changed?
   *
   *  Mine, not already withdrawn, and inside the window the SERVER sent us. An
   *  unreadable stamp answers "no" — the same direction as `chat_message_age_seconds`
   *  on the backend, and the same reason: a stamp nobody can read must not become a
   *  message that is editable forever. */
  const withinEditWindow = useCallback((m) => {
    if (!me || m.from_user !== me.user_id || isTombstone(m)) return false;
    const sent = parseStamp(m.created_at);
    if (!sent) return false;
    return (Date.now() - sent.getTime()) / 1000 <= editWindowSeconds;
  }, [me, editWindowSeconds]);

  const personId = person?.user_id;

  /** Run the search. Debounced by the effect below, never on every keystroke — each one
   *  is a query against the shared SQLite file (Critical Note #17). */
  useEffect(() => {
    if (!searchOpen) return undefined;
    const q = query.trim();
    if (!q) { setResults(null); setSearching(false); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const found = await onSearch(personId, q);
        if (!cancelled) setResults(found);
      } catch (_) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, searchOpen, personId, onSearch]);

  if (!person) return null;

  /** Send. `context` is passed explicitly so "Share this view" can send its card in the
   *  same click, without waiting for a state update it would otherwise race. */
  const send = async (body, context) => {
    if (!body && !context) return;
    setBusy(true);
    setError('');
    try {
      // A message sent into a search window belongs at the live end of the conversation,
      // so go there first — otherwise it is stored, and invisible.
      if (hasMoreAfter) await onJumpToLatest?.(person.user_id);
      await onSend(person.user_id, body, context || undefined);
      setText('');
      setAttached(null);
    } catch (err) {
      setError(err.message || 'Could not send that message.');
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    send(text.trim(), attached);
  };

  /** ⚠️ "Share this view" SENDS. It used to only attach the card, leaving a chip above the
   *  composer that the sender then had to send — and the first person to use it in anger
   *  read the chip as "done", never pressed send, and the card was silently never shared
   *  (verified: every row in `chat_messages` had an empty `context`). The button says
   *  "Share this view", so it shares this view; whatever is already typed goes with it. */
  const shareThisView = () => {
    const ctx = buildCurrentView();
    if (!ctx) return;
    send(text.trim(), ctx);
  };

  const loadEarlier = async () => {
    const el = scrollRef.current;
    // Measured BEFORE the request, because the layout effect that restores it runs after
    // the new rows are in the DOM and `scrollHeight` has already grown.
    scrollIntentRef.current = { type: 'hold', height: el ? el.scrollHeight - el.scrollTop : 0 };
    setLoadingEarlier(true);
    setError('');
    try {
      await onLoadEarlier(person.user_id);
    } catch (err) {
      scrollIntentRef.current = { type: 'bottom' };
      setError(err.message || 'Could not load earlier messages.');
    } finally {
      setLoadingEarlier(false);
    }
  };

  const goToHit = async (messageId) => {
    setError('');
    scrollIntentRef.current = { type: 'anchor', id: messageId };
    try {
      await onJumpToMessage(person.user_id, messageId);
      setSearchOpen(false);
    } catch (err) {
      scrollIntentRef.current = { type: 'bottom' };
      setError(err.message || 'That message could not be opened.');
    }
  };

  const goToLatest = async () => {
    setError('');
    try {
      await onJumpToLatest(person.user_id);
    } catch (err) {
      setError(err.message || 'Could not return to the latest messages.');
    }
  };

  const startEdit = (m) => {
    setPendingDeleteId('');
    setEditingId(m.id);
    setEditText(m.body || '');
    setError('');
  };

  const saveEdit = async (e) => {
    e?.preventDefault();
    const body = editText.trim();
    if (!body) {
      setError('An edited message cannot be empty — delete it instead.');
      return;
    }
    setBusy(true);
    try {
      await onEdit(editingId, body);
      setEditingId('');
    } catch (err) {
      setError(err.message || 'Could not save that change.');
    } finally {
      setBusy(false);
    }
  };

  /** Deleting asks first, inline. A message is the one thing in this product that cannot
   *  be rebuilt from Orcanos (Critical Note #70), and a mis-aimed 🗑 on a hover toolbar is
   *  an easy click to make. */
  const confirmDelete = async (messageId) => {
    setBusy(true);
    setError('');
    try {
      await onDelete(messageId);
      setPendingDeleteId('');
    } catch (err) {
      setError(err.message || 'Could not delete that message.');
    } finally {
      setBusy(false);
    }
  };

  const renderMessage = (m) => {
    const mine = m.from_user === me?.user_id;
    const editable = withinEditWindow(m);
    const gone = isTombstone(m);

    if (editingId === m.id) {
      return (
        <div key={m.id} className={`collab-msg${mine ? ' collab-msg--mine' : ''}`}>
          <form className="collab-edit" onSubmit={saveEdit}>
            <input
              type="text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(''); }}
              maxLength={4000}
              disabled={busy}
              aria-label="Edit message"
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
            />
            <div className="collab-edit-actions">
              <button type="button" className="collab-edit-cancel"
                      onClick={() => setEditingId('')} disabled={busy}>Cancel</button>
              <button type="submit" className="collab-edit-save" disabled={busy}>Save</button>
            </div>
          </form>
        </div>
      );
    }

    return (
      <div
        key={m.id}
        ref={(node) => { bodyIdRef.current[m.id] = node; }}
        className={`collab-msg${mine ? ' collab-msg--mine' : ''}`
          + (anchorId === m.id ? ' collab-msg--hit' : '')}
      >
        {m.context && <ContextCard context={m.context} onNavigate={onClose} />}

        {gone ? (
          <div className="collab-bubble collab-bubble--gone">Message deleted</div>
        ) : (
          m.body && <div className="collab-bubble">{m.body}</div>
        )}

        <div className="collab-msg-time">
          {formatStamp(m.created_at)}
          {m.edited_at && !gone && <span className="collab-edited"> · edited</span>}

          {editable && pendingDeleteId !== m.id && (
            <span className="collab-msg-actions">
              <button type="button" onClick={() => startEdit(m)} title="Edit message"
                      aria-label="Edit message">✎</button>
              <button type="button" onClick={() => { setEditingId(''); setPendingDeleteId(m.id); }}
                      title="Delete message" aria-label="Delete message">🗑</button>
            </span>
          )}
        </div>

        {pendingDeleteId === m.id && (
          <div className="collab-confirm" role="alert">
            <span>Delete this message?</span>
            <button type="button" onClick={() => confirmDelete(m.id)} disabled={busy}>Delete</button>
            <button type="button" className="collab-confirm-no"
                    onClick={() => setPendingDeleteId('')} disabled={busy}>Keep</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="collab-dock" role="complementary" aria-label={`Chat with ${person.display_name}`}>
      <div className="collab-dock-head">
        <span
          className={`collab-av collab-dock-avatar${person.online ? ' collab-av--online' : ''}`}
          style={avatarStyle(person.avatar_color)}
          aria-hidden="true"
        >
          {person.initials}
        </span>
        <span className="collab-dock-who">
          <span className="collab-dock-name">{person.display_name}</span>
          <span className="collab-dock-where">
            {person.online ? (person.where ? `viewing ${person.where}` : 'Online') : 'Offline'}
          </span>
        </span>
        <button
          type="button"
          className={`collab-dock-search-btn${searchOpen ? ' is-on' : ''}`}
          onClick={() => setSearchOpen((v) => !v)}
          aria-label={searchOpen ? 'Close search' : 'Search this conversation'}
          aria-pressed={searchOpen}
          title="Search this conversation"
        >⌕</button>
        <button type="button" className="collab-dock-x" onClick={onClose} aria-label="Close chat">×</button>
      </div>

      {searchOpen && (
        <div className="collab-search">
          <input
            ref={searchRef}
            type="search"
            className="collab-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setSearchOpen(false); }}
            placeholder={`Search messages with ${person.display_name.split(' ')[0]}…`}
            aria-label="Search this conversation"
          />
          {query.trim() !== '' && (
            <div className="collab-search-results">
              {searching && <div className="collab-search-note">Searching…</div>}
              {!searching && results && results.length === 0 && (
                <div className="collab-search-note">No message matches “{query.trim()}”.</div>
              )}
              {!searching && results && results.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="collab-search-hit"
                  onClick={() => goToHit(m.id)}
                >
                  <span className="collab-search-hit-who">
                    {m.from_user === me?.user_id ? 'You' : person.display_name.split(' ')[0]}
                  </span>
                  <span className="collab-search-hit-body">{m.body}</span>
                  <span className="collab-search-hit-when">{formatStamp(m.created_at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="collab-dock-body" ref={scrollRef}>
        {hasMoreBefore && (
          <button type="button" className="collab-more" onClick={loadEarlier}
                  disabled={loadingEarlier}>
            {loadingEarlier ? 'Loading…' : 'Load earlier messages'}
          </button>
        )}

        {thread.length === 0 && (
          <div className="collab-empty">
            No messages yet. Share what you are looking at — they will land on the same row.
          </div>
        )}

        {thread.map(renderMessage)}
      </div>

      {/* Only shown when the thread is a WINDOW out of the middle of the conversation, so
          the reader is never told to "jump to latest" when they are already there. */}
      {hasMoreAfter && (
        <button type="button" className="collab-jump" onClick={goToLatest}>
          ↓ Jump to latest
        </button>
      )}

      <form className="collab-dock-foot" onSubmit={submit}>
        {error && <div className="collab-refusal" role="alert">{error}</div>}

        {/* A PENDING card only ever comes from "Discuss with…", where the row was picked
            before there was anything to say about it. It states that it has not been sent
            yet, because a chip that merely says "Attached" reads as "done". */}
        {attached ? (
          <div className="collab-attached">
            <span className="collab-attached-text">
              {attached.kind === 'row'
                ? (attached.focus?.key || 'This row')
                : 'This view'}
            </span>
            <span className="collab-attached-hint">goes with your message</span>
            <button type="button" className="collab-attached-x" onClick={() => setAttached(null)}
                    aria-label="Remove attachment">×</button>
          </div>
        ) : canShareView && (
          <button type="button" className="collab-chip" onClick={shareThisView} disabled={busy}>
            ＋ Share this view
          </button>
        )}

        <div className="collab-composer">
          <label htmlFor="collab-composer-input" className="collab-sr">
            Message {person.display_name}
          </label>
          <input
            id="collab-composer-input"
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Message ${person.display_name.split(' ')[0]}…`}
            maxLength={4000}
            disabled={busy}
          />
          <button type="submit" className="collab-send" disabled={busy || (!text.trim() && !attached)}
                  aria-label="Send">↑</button>
        </div>

        <div className="collab-retention">
          {retentionDays > 0
            ? `Messages are deleted after ${retentionDays} days.`
            : 'Messages are kept indefinitely.'}
        </div>
      </form>
    </div>
  );
}

export default ChatDock;
